import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';

interface ParticleFieldProps {
  quantity?: number;
  className?: string;
}

interface Fragment {
  x: number;
  y: number;
  vy: number;
  drift: number;
  size: number;
  rotation: number;
  rotationSpeed: number;
  progress: number; // 0 = plastic fragment (grey), 1 = leaf (green)
  progressSpeed: number;
}

/**
 * Ambient canvas background: small plastic fragments drift slowly upward and
 * soften into green leaf-colored flecks as they rise — a visual echo of
 * "turning waste into worth". Slow, ambient, never distracting.
 */
export function ParticleField({ quantity = 34, className }: ParticleFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let fragments: Fragment[] = [];
    let w = 0;
    let h = 0;
    let raf = 0;

    const greyRGB = [138, 143, 140];
    const greenRGB = [20, 241, 149];

    const makeFragment = (randomY = true): Fragment => ({
      x: Math.random() * w,
      y: randomY ? Math.random() * h : h + 20,
      vy: -(Math.random() * 0.22 + 0.06),
      drift: (Math.random() - 0.5) * 0.18,
      size: Math.random() * 3.5 + 2,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.01,
      progress: Math.random() * 0.3,
      progressSpeed: Math.random() * 0.0009 + 0.0003,
    });

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (!rect) return;
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fragments = Array.from({ length: quantity }, () => makeFragment());
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const f of fragments) {
        f.y += f.vy;
        f.x += f.drift;
        f.rotation += f.rotationSpeed;
        f.progress = Math.min(1, f.progress + f.progressSpeed);

        if (f.y < -20) {
          Object.assign(f, makeFragment(false));
        }

        const r = greyRGB[0] + (greenRGB[0] - greyRGB[0]) * f.progress;
        const g = greyRGB[1] + (greenRGB[1] - greyRGB[1]) * f.progress;
        const b = greyRGB[2] + (greenRGB[2] - greyRGB[2]) * f.progress;
        const alpha = 0.12 + f.progress * 0.28;

        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rotation);
        ctx.fillStyle = `rgba(${r.toFixed(0)},${g.toFixed(0)},${b.toFixed(0)},${alpha.toFixed(3)})`;
        const s = f.size;
        // Fragment softens from a sharp square (plastic) to a rounded leaf-drop shape
        const radius = s * 0.15 + f.progress * s * 0.55;
        ctx.beginPath();
        ctx.moveTo(-s + radius, -s);
        ctx.arcTo(s, -s, s, s, radius);
        ctx.arcTo(s, s, -s, s, radius);
        ctx.arcTo(-s, s, -s, -s, radius);
        ctx.arcTo(-s, -s, s, -s, radius);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    raf = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [quantity]);

  return (
    <canvas
      ref={canvasRef}
      className={cn('pointer-events-none absolute inset-0 h-full w-full', className)}
      aria-hidden='true'
    />
  );
}

export default ParticleField;
